import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PdfSignEmbed } from "./pdf-sign-embed";
import type { FormAnchorRef } from "../form-anchor-ref";

interface PdfSignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  /** The assignment or hiring-process item this signature fulfills. */
  anchor: FormAnchorRef;
  /** Called once the signature is recorded (or the doc is fully filled). */
  onSigned?: () => void;
  title?: string;
}

/**
 * Dialog wrapper around `PdfSignEmbed` for the org's (or hiring-process
 * candidate's) signing surface. The candidate signs a chat assignment via
 * their own surface in `form-submission-dialog.tsx`.
 */
export function PdfSignDialog({
  open,
  onOpenChange,
  formId,
  anchor,
  onSigned,
  title = "Sign document",
}: PdfSignDialogProps) {
  const [isBusy, setIsBusy] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // While busy the embed may be mid prefill-apply: unmounting then
        // would write values into the vendor document with the disclosure
        // note never shown. Mirrors the disabled Cancel button for Escape,
        // the overlay, and the built-in X.
        if (!nextOpen && isBusy) {
          return;
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="h-[90vh] flex flex-col overflow-hidden"
        style={{ width: "90vw", maxWidth: "none" }}
      >
        <DialogTitle>{title}</DialogTitle>
        <div className="flex-1 min-h-0 overflow-auto">
          {open && (
            <PdfSignEmbed
              formId={formId}
              anchor={anchor}
              onBusyChange={setIsBusy}
              onSigned={(res) => {
                toast.success(
                  res.fillStatus === "filled"
                    ? "Document complete"
                    : res.signerStatus.candidate === "pending"
                      ? "Signature recorded — waiting for the provider"
                      : "Signature recorded",
                );
                onSigned?.();
                onOpenChange(false);
              }}
              onError={(message) => toast.error(message)}
            />
          )}
        </div>
        <div className="shrink-0 flex items-center justify-between gap-3 pt-2">
          <p className="text-xs text-gray-500">
            Fill in your fields and complete the form to sign.
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isBusy}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
